/**
   * ------------------------------------------------------------
   * COMPUTE navlog outputs per row
   * 
   * memoize the computed navlog legs so that they are only recalculated
   * when the input values (navlogRows and forecastTerminals) change.
   * 
   * note: map creates a new array
   * for each row in the navlog, it finds the corresponding forecast terminal data
   *  ------------------------------------------------------------
   */


import {
  deg2rad,
  rad2deg,
  norm180,
  norm360,
  normalizeFt,
  interpolation,
  tasFromCas,
} from "./navlogMath";

/**
 * ------------------------------------------------------------
 * COMPUTE navlog outputs per row
 *
 * For each navlog row:
 * 1. find matching forecast terminal data
 * 2. interpolate winds at cruise altitude
 * 3. compute TAS
 * 4. compute WCA / TH / MH / CH
 * 5. compute GS, ETE, and fuel burn
 * 6. compute HW and CW components
 * ------------------------------------------------------------
 */
export function computeNavlogLegs(navlogRows, forecastTerminals) {
  return navlogRows.map((row) => {
    const ftData = forecastTerminals.find(
      (f) => normalizeFt(f.ft) === normalizeFt(row.ft)
    );

    const cruiseAltitude = Number(row.cruiseAlt);
    const CAS = Number(row.cas);
    const TC = Number(row.tc);
    const NM = Number(row.nm);

    const rowOk =
      Number.isFinite(cruiseAltitude) &&
      Number.isFinite(CAS) &&
      Number.isFinite(TC) &&
      CAS > 0;

    // ------------------------------------------------------------
    // Missing or invalid row data
    // ------------------------------------------------------------
    if (!ftData || !rowOk) {
      return {
        ...row,
        tas: "",
        windDir: "",
        windSpd: "",
        tempC: "",
        wca: "",
        th: "",
        mh: "",
        ch: "",
        gs: "",
        eteMin: "",
        galsUsed: "",
        gph: row.gph ?? "",
        hw: "",
        cw: "",
        hwcw: "",
      };
    }

    // ------------------------------------------------------------
    // Forecast terminal values
    // ------------------------------------------------------------
    const A1 = Number(ftData.alt1);
    const A2 = Number(ftData.alt2);
    const A3 = Number(ftData.alt3);

    const WD1 = Number(ftData.wd1);
    const WD2 = Number(ftData.wd2);
    const WD3 = Number(ftData.wd3);

    const WS1 = Number(ftData.ws1);
    const WS2 = Number(ftData.ws2);
    const WS3 = Number(ftData.ws3);

    const T1 = Number(ftData.t1);
    const T2 = Number(ftData.t2);
    const T3 = Number(ftData.t3);

    const requiredWinds = [
      A1,
      A2,
      A3,
      WD1,
      WD2,
      WD3,
      WS1,
      WS2,
      WS3,
      T1,
      T2,
      T3,
    ];

    const windsOk = requiredWinds.every(Number.isFinite);

    // ------------------------------------------------------------
    // Missing wind data
    // ------------------------------------------------------------
    if (!windsOk) {
      return {
        ...row,
        tas: "",
        windDir: "",
        windSpd: "",
        tempC: "",
        wca: "",
        th: "",
        mh: "",
        ch: "",
        gs: "",
        eteMin: "",
        galsUsed: "",
        gph: row.gph ?? "",
        hw: "",
        cw: "",
        hwcw: "",
      };
    }

    // ------------------------------------------------------------
    // TAS
    // ------------------------------------------------------------
    const TAS = tasFromCas(CAS, cruiseAltitude);

    // ------------------------------------------------------------
    // Interpolate winds at cruise altitude
    // ------------------------------------------------------------
    const windDirInterp = interpolation(
      cruiseAltitude,
      A1,
      A2,
      A3,
      WD1,
      WD2,
      WD3
    );

    const windSpeedInterp = interpolation(
      cruiseAltitude,
      A1,
      A2,
      A3,
      WS1,
      WS2,
      WS3
    );

    // ------------------------------------------------------------
    // Interpolate temperature
    // ------------------------------------------------------------
    const isaTemp = 15 - (2 * cruiseAltitude) / 1000.0;

    const tempInterp =
      Number(cruiseAltitude) <= 3000
        ? isaTemp
        : interpolation(cruiseAltitude, A1, A2, T1, T2);

    // ------------------------------------------------------------
    // Wind correction angle
    // ------------------------------------------------------------
    const windAngleDeg = windDirInterp - TC;

    const angle = norm180(windAngleDeg);

    const radians = deg2rad(angle);

    // ------------------------------------------------------------
    // Headwind and crosswind components
    // ------------------------------------------------------------
    const HW = windSpeedInterp * Math.cos(radians);

    const CW = windSpeedInterp * Math.sin(radians);

    // +HW = headwind
    // -HW = tailwind
    // +CW = wind from right
    // -CW = wind from left

    const hw = HW;
    const cw = CW;

    // ------------------------------------------------------------
    // Groundspeed
    // ------------------------------------------------------------
    const GS = TAS - HW;

    // ------------------------------------------------------------
    // WCA
    // ------------------------------------------------------------
    const ratio = CW / TAS;

    const wcaDeg =
      Math.abs(ratio) > 1
        ? ""
        : rad2deg(Math.asin(ratio));

    // ------------------------------------------------------------
    // True heading
    // ------------------------------------------------------------
    const TH =
      wcaDeg === ""
        ? ""
        : TC + wcaDeg;

    // ------------------------------------------------------------
    // Magnetic and compass heading
    // ------------------------------------------------------------
    const mdev = Number(row.mdev);
    const cdev = Number(row.cdev);

    const MH =
      TH !== "" && Number.isFinite(mdev)
        ? norm360(TH + mdev)
        : "";

    const CH =
      MH !== "" && Number.isFinite(cdev)
        ? norm360(MH + cdev)
        : "";

    // ------------------------------------------------------------
    // ETE
    // ------------------------------------------------------------
    const eteMin =
      Number.isFinite(NM) &&
      NM > 0 &&
      Number.isFinite(GS) &&
      GS > 0
        ? (NM / GS) * 60
        : "";

    // ------------------------------------------------------------
    // Fuel burn
    // ------------------------------------------------------------
    const gph = Number(row.gph);

    const gallonsUsed =
      Number.isFinite(gph) &&
      Number.isFinite(eteMin)
        ? (gph * eteMin) / 60
        : "";

    // ------------------------------------------------------------
    // Return computed row
    // ------------------------------------------------------------
    return {
      ...row,

      tas: TAS,

      windDir: windDirInterp,
      windSpd: windSpeedInterp,
      tempC: tempInterp,

      wca: wcaDeg,

      th: TH,
      mh: MH,
      ch: CH,

      gs: GS,

      eteMin: eteMin,

      galsUsed: gallonsUsed,

      gph: row.gph ?? "",

      hw: hw,
      cw: cw,

      hwcw: `${Number.isFinite(hw) ? hw.toFixed(0) : ""}/${
        Number.isFinite(cw) ? cw.toFixed(0) : ""
      }`,
    };
  });
}